/**
 * LoopNest Scene Builder
 * Builds BPM-aware scene timelines for realistic visual styles.
 * Analyzes song BPM, divides timeline into scenes, assigns clip categories.
 */

import { unsplashProvider, SCENE_CATEGORIES } from '../providers/unsplashProvider';
import mediaProviderRegistry from '../providers/mediaProviderRegistry';

// Scene pacing templates based on BPM
const PACING_TEMPLATES = {
  slow: { // < 80 BPM
    sceneDuration: 20,
    transitionStyle: 'fade',
    description: 'slow, contemplative pacing',
  },
  medium: { // 80-120 BPM
    sceneDuration: 12,
    transitionStyle: 'dissolve',
    description: 'moderate pacing',
  },
  fast: { // 120-160 BPM
    sceneDuration: 8,
    transitionStyle: 'cut',
    description: 'energetic pacing',
  },
  veryFast: { // > 160 BPM
    sceneDuration: 5,
    transitionStyle: 'cut',
    description: 'rapid pacing',
  },
};

/**
 * Get pacing template based on BPM
 */
export function getPacingTemplate(bpm) {
  if (!bpm || bpm < 80) return PACING_TEMPLATES?.slow;
  if (bpm < 120) return PACING_TEMPLATES?.medium;
  if (bpm < 160) return PACING_TEMPLATES?.fast;
  return PACING_TEMPLATES?.veryFast;
}

/**
 * Assign scene arc categories based on position in song
 * Creates a narrative arc: establishing → development → climax → resolution
 */
export function assignSceneArc(sceneIndex, totalScenes, categories) {
  const cats = categories?.length ? categories : SCENE_CATEGORIES?.default;
  const position = sceneIndex / Math.max(1, totalScenes - 1); // 0.0 to 1.0

  // Narrative arc mapping
  if (position < 0.15) {
    // Establishing shot (first 15%)
    return { ...cats?.[0], arcRole: 'establishing' };
  } else if (position < 0.4) {
    // Development (15-40%)
    const idx = 1 % cats?.length;
    return { ...cats?.[idx], arcRole: 'development' };
  } else if (position < 0.65) {
    // Mid-section (40-65%)
    const idx = 2 % cats?.length;
    return { ...cats?.[idx], arcRole: 'mid' };
  } else if (position < 0.85) {
    // Climax (65-85%)
    const idx = Math.min(3, cats?.length - 1);
    return { ...cats?.[idx], arcRole: 'climax' };
  } else {
    // Resolution (85-100%)
    const idx = cats?.length - 1;
    return { ...cats?.[idx], arcRole: 'resolution' };
  }
}

/**
 * Build a complete BPM-aware scene timeline for a given style and duration
 * @param {object} params
 * @param {number} params.duration - Total video duration in seconds
 * @param {number} params.bpm - Song BPM
 * @param {string} params.styleId - Visual style ID
 * @param {string[]} params.keywords - Semantic keywords from song analysis
 * @returns {SceneEntry[]}
 */
export function buildSceneTimeline(params) {
  const { duration, bpm = 120, styleId = 'nature', keywords = [] } = params;

  const pacing = getPacingTemplate(bpm);
  const categories = unsplashProvider?.getSceneCategories(styleId);

  // Calculate number of scenes based on pacing
  const sceneCount = Math.max(3, Math.ceil(duration / pacing?.sceneDuration));
  const scenes = [];

  for (let i = 0; i < sceneCount; i++) {
    const startTime = (i / sceneCount) * duration;
    const endTime = ((i + 1) / sceneCount) * duration;
    const sceneDuration = endTime - startTime;

    // Assign scene category based on arc position
    const arcScene = assignSceneArc(i, sceneCount, categories);

    // Blend with semantic keywords if available
    const keyword = keywords?.[i % keywords?.length]
      ? `${arcScene?.keyword} ${keywords?.[i % keywords?.length]}`
      : arcScene?.keyword;

    // Build image URL using provider registry
    const imageUrl = mediaProviderRegistry?.getSceneImageUrl(
      keyword,
      1280,
      720,
      i + 1 // seed for variety
    );

    // Transition type based on pacing and position
    let transition = pacing?.transitionStyle;
    if (i === 0) transition = 'fade'; // Always fade in
    if (i === sceneCount - 1) transition = 'fade'; // Always fade out

    scenes?.push({
      index: i,
      startTime,
      endTime,
      duration: sceneDuration,
      keyword,
      arcRole: arcScene?.arcRole,
      desc: arcScene?.desc,
      imageUrl,
      transition,
      styleId,
      bpm,
    });
  }

  return scenes;
}

/**
 * Get human-readable scene description for a timeline
 * Used for debugging and UI display
 */
export function describeTimeline(scenes) {
  return scenes?.map((s) => {
    const start = Math.round(s?.startTime);
    const end = Math.round(s?.endTime);
    return `${start}-${end}s: ${s?.desc} (${s?.arcRole})`;
  });
}

/**
 * Preload scene images with timeout and fallback
 * @param {SceneEntry[]} timeline
 * @param {number} timeoutMs - Per-image timeout
 * @returns {Promise<Map<number, HTMLImageElement>>}
 */
export async function preloadSceneImages(timeline, timeoutMs = 6000) {
  const map = new Map();

  const promises = timeline?.map((scene) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timer = setTimeout(() => {
        // Timeout: try AI-generated fallback
        resolve();
      }, timeoutMs);

      img.onload = () => {
        clearTimeout(timer);
        if (img.naturalWidth > 0) {
          map.set(scene.index, img);
        }
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timer);
        resolve(); // Gracefully skip
      };

      img.src = scene.imageUrl;
    });
  });

  await Promise.all(promises);
  return map;
}
